// Activiteiten: examens, cursussen en clinics met inschrijving vanuit het
// ledenbestand (stap J), en aanvragen van leden en ouders goedkeuren of
// afwijzen (zelf inschrijven, fase 1).
//
// Eigen module, buiten app.js, met events via data-act en delegatie. Het slot
// zit in de database: public.inschrijven controleert per persoon status,
// termijn, schorsing, leeftijd, graad, licentie en maximum; geld en uitslag
// lopen via de dispatcher. Dit scherm legt voor en toont per persoon waarom
// iets niet kon.
//
// Toernooien staan in de lijst omdat elk event een activiteit heeft, maar ze
// worden beheerd in de module Toernooien tot stap M.
import { getClient } from '../data/supabase.js';
import { state } from '../state.js';
import { heeft, heeftModule } from '../router.js';
import { t, locale } from '../lib/i18n.js';
import { esc, fmtMoney } from '../lib/format.js';
import { toast } from '../ui/toast.js';
import { showConfirm } from '../ui/confirm.js';
import { tabelTeken } from '../ui/tabel.js';
import { inschrijvingStatus } from '../lib/money.js';
import { dispatchFout, vernieuwTeller } from './goedkeuringen.js';

const sb = () => getClient();
export const SOORTEN = ['examen', 'cursus', 'clinic'];
export const STATUSSEN = ['concept', 'open', 'gesloten', 'afgerond', 'geannuleerd'];

let activiteiten = [];
let huidige = null;          // id van de geopende activiteit
let deelnemers = [];
let schaal = [];
let soorten = [];

// ── Hulpjes ────────────────────────────────────────────────────────────────
function datum(iso) {
  if (!iso) return '';
  const d = new Date(iso + 'T00:00:00Z');
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString(locale(), { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' });
}
function periode(a) {
  if (!a.start_datum) return '';
  return a.eind_datum && a.eind_datum !== a.start_datum ? `${datum(a.start_datum)} - ${datum(a.eind_datum)}` : datum(a.start_datum);
}
const geld = n => `€ ${fmtMoney(n)}`;
const knop = (act, icoon, tekst, soort = 'btn-default', extra = '') =>
  `<button type="button" class="btn ${soort} btn-sm" data-act="${act}" ${extra}><i class="ti ti-${icoon}"></i> ${esc(tekst)}</button>`;
const klein = tekst => `<p style="font-size:12px;color:var(--text-3);margin:4px 0">${esc(tekst)}</p>`;
const bond = () => heeftModule('bond');

// Foutcodes uit de database: inschrijving:, uitslag:, activiteit:, dispatch:.
export function activiteitFout(e) {
  const m = String(e?.message || e || '').match(/(inschrijving|uitslag|activiteit):([a-z_]+)/);
  return m ? t(`${m[1]}.fout.${m[2]}`) : dispatchFout(e);
}
function fout(e) { toast(activiteitFout(e), 'error'); }

const STATUSKLEUR = { concept: 'badge-grey', open: 'badge-green', gesloten: 'badge-amber', afgerond: 'badge-teal', geannuleerd: 'badge-red' };
const statusBadge = s => `<span class="badge ${STATUSKLEUR[s] || 'badge-grey'}">${esc(t('act.status.' + s))}</span>`;
const BETAALKLEUR = { paid: 'badge-green', partial: 'badge-amber', unpaid: 'badge-red', nvt: 'badge-grey' };
function betaalBadge(r) {
  const s = inschrijvingStatus(r);
  const tekst = r.settlement === 'written_off' ? t('act.betaal.afgeboekt') : t('act.betaal.' + s);
  return `<span class="badge ${BETAALKLEUR[s] || 'badge-grey'}">${esc(tekst)}</span>`;
}
function uitslagTekst(r) {
  if (!r.uitslag) return '';
  const graad = r.uitslag_code ? ' ' + (schaal.find(s => s.code === r.uitslag_code)?.naam || r.uitslag_code) : '';
  return t('act.uitslag.' + r.uitslag) + (r.uitslag === 'geslaagd' ? graad : '');
}
const UITSLAGKLEUR = { voorgesteld: 'badge-amber', vastgelegd: 'badge-green', afgewezen: 'badge-red', geen_register: 'badge-grey' };
const uitslagBadge = r => r.uitslag_status
  ? ` <span class="badge ${UITSLAGKLEUR[r.uitslag_status]}">${esc(t('act.uitslagstatus.' + r.uitslag_status))}</span>` : '';

// Status naast de naam: aanvraag, afgewezen, afgemeld.
function deelnemerBadges(d) {
  if (d.status === 'aangevraagd') return ` <span class="badge badge-amber">${esc(t('act.aangevraagd'))}</span>`;
  if (d.status === 'afgewezen') return ` <span class="badge badge-red">${esc(t('act.afgewezen'))}</span>`;
  if (d.status === 'afgemeld') return ` <span class="badge badge-grey">${esc(t('act.afgemeld'))}</span>`;
  return '';
}

// ── Modal ──────────────────────────────────────────────────────────────────
function modal() {
  let m = document.getElementById('actModal');
  if (!m) {
    m = document.createElement('div');
    m.className = 'modal-overlay';
    m.id = 'actModal';
    m.innerHTML = '<div class="modal" style="max-width:760px"><div id="actModalInhoud" style="display:flex;flex-direction:column;min-height:0;flex:1"></div></div>';
    document.body.appendChild(m);
    m.addEventListener('click', e => {
      if (e.target === m || e.target.closest('[data-act="sluit"]')) { sluitModal(); return; }
      klik(e);
    });
    m.addEventListener('submit', e => e.preventDefault());
    m.addEventListener('input', e => { if (e.target.matches('[data-zoek]')) filterKandidaten(e.target.value); });
    m.addEventListener('change', e => {
      if (!e.target.matches('[name="kind"]')) return;
      toonVeldenVoorSoort();
      // Standaard: akkoord van de club bij een examen, niet bij cursus of clinic.
      const akkoord = m.querySelector('[name="akkoord_nodig"]');
      if (akkoord) akkoord.checked = e.target.value === 'examen';
    });
  }
  return m;
}
function toonModal(titel, lichaam, voet) {
  const m = modal();
  m.querySelector('#actModalInhoud').innerHTML = `
    <div class="modal-header"><span class="modal-title">${esc(titel)}</span>
      <button type="button" class="btn-icon" data-act="sluit" title="${esc(t('alg.sluiten'))}"><i class="ti ti-x"></i></button></div>
    <div class="modal-body">${lichaam}</div>
    ${voet ? `<div class="modal-footer">${voet}</div>` : ''}`;
  m.classList.add('open');
  return m.querySelector('#actModalInhoud');
}
function sluitModal() { document.getElementById('actModal')?.classList.remove('open'); }
const waarde = (el, naam) => el.querySelector(`[name="${naam}"]`)?.value ?? '';

// ── Laden ──────────────────────────────────────────────────────────────────
async function laadRegister() {
  const [g, l] = await Promise.all([
    sb().from('graduatieschaal').select('code, naam, volgorde').eq('organisation_id', state.currentOrgId),
    bond() ? sb().from('licence_types').select('id, naam, actief').eq('organisation_id', state.currentOrgId) : Promise.resolve({ data: [] }),
  ]);
  schaal = [...(g.data || [])].sort((a, b) => a.volgorde - b.volgorde);
  soorten = (l.data || []).filter(s => s.actief);
}

export async function render() {
  const el = document.getElementById('activiteitenInhoud');
  if (!el) return;
  koppel(el);
  el.innerHTML = '<div class="spinner"></div>';
  await laadRegister();
  if (huidige) return renderDetail(el);
  return renderLijst(el);
}

// ── Lijst ──────────────────────────────────────────────────────────────────
async function renderLijst(el) {
  // Toernooien staan in de module Toernooien. Hun koppeling met activiteiten is
  // voor overzichten over alle soorten heen, niet voor deze lijst.
  const { data, error } = await sb().from('activiteiten_overzicht').select('*')
    .eq('organisation_id', state.currentOrgId).neq('kind', 'toernooi').order('start_datum', { ascending: false, nullsFirst: true });
  if (error) {
    el.innerHTML = `<div class="banner banner-amber"><i class="ti ti-alert-triangle"></i><span>${esc(t('act.nietbeschikbaar'))}</span></div>`;
    return;
  }
  activiteiten = (data || []).filter(a => a.kind !== 'toernooi');
  el.innerHTML = `
    <div style="display:flex;align-items:center;gap:8px;margin-bottom:16px;flex-wrap:wrap">
      ${heeft('activity.manage') ? knop('nieuw', 'plus', t('act.nieuw'), 'btn-primary') : ''}
      <span style="font-size:12px;color:var(--text-3)">${esc(t('act.uitleg'))}</span>
    </div>
    <div id="actTabel"></div>`;
  tabelTeken('activiteiten', 'actTabel', [
    { veld: 'naam', label: t('act.kol.naam'),
      cel: a => `<strong>${esc(a.naam)}</strong>${a.kind === 'toernooi' ? ` <span class="badge badge-grey">${esc(t('act.toernooi.badge'))}</span>` : ''}` },
    { veld: 'kind', label: t('act.kol.soort'), tekst: a => t('act.soort.' + a.kind) },
    { veld: 'start_datum', label: t('act.kol.datum'), filter: false, tekst: a => a.start_datum || '', cel: a => esc(periode(a)) },
    { veld: 'status', label: t('act.kol.status'), tekst: a => t('act.status.' + a.status), cel: a => statusBadge(a.status) },
    { veld: 'deelnemers', label: t('act.kol.deelnemers'), uitlijn: 'right', mono: true, filter: false,
      tekst: a => a.kind === 'toernooi' ? String(a.teams ?? '') : String(a.deelnemers ?? 0),
      cel: a => esc(a.kind === 'toernooi' ? `${a.teams ?? 0} ${t('act.teams')}` : `${a.deelnemers ?? 0}${a.max_deelnemers ? ' / ' + a.max_deelnemers : ''}`)
        + (Number(a.aanvragen) ? ` <span class="badge badge-amber">${esc(t('act.aanvragen.aantal', { aantal: a.aanvragen }))}</span>` : '') },
    { veld: 'tarief_eur', label: t('act.kol.tarief'), uitlijn: 'right', mono: true, filter: false,
      tekst: a => a.kind === 'toernooi' ? '' : String(a.tarief_eur ?? 0), cel: a => a.kind === 'toernooi' ? '' : esc(geld(a.tarief_eur)) },
    { veld: 'openstaand_eur', label: t('act.kol.openstaand'), uitlijn: 'right', mono: true, filter: false,
      tekst: a => a.kind === 'toernooi' ? '' : String(a.openstaand_eur ?? 0), cel: a => a.kind === 'toernooi' ? '' : esc(geld(a.openstaand_eur)) },
  ], activiteiten, {
    zoekLabel: t('act.zoek'),
    leeg: t('act.leeg'),
    rijKlik: a => {
      if (a.kind === 'toernooi') { toast(t('act.toernooi.uitleg')); return; }
      huidige = a.id; render();
    },
  });
}

// ── Formulier voor een activiteit ──────────────────────────────────────────
function openFormulier(a = null) {
  const nieuw = !a;
  const v = a || { kind: 'examen', status: 'concept', tarief_eur: 0, zelf_inschrijven: false, akkoord_nodig: true };
  const optie = (w, tekst, gekozen) => `<option value="${esc(w)}"${gekozen ? ' selected' : ''}>${esc(tekst)}</option>`;
  const veld = (naam, label, type = 'text', extra = '') => `<div class="form-group"><label class="form-label">${esc(label)}</label>
      <input class="form-input" type="${type}" name="${naam}" value="${esc(v[naam] == null ? '' : String(v[naam]))}" ${extra}></div>`;
  const inhoud = `
    <div class="form-row">
      <div class="form-group"><label class="form-label">${esc(t('act.veld.soort'))}</label>
        <select class="form-input" name="kind" ${nieuw ? '' : 'disabled'}>${SOORTEN.map(s => optie(s, t('act.soort.' + s), v.kind === s)).join('')}</select></div>
      <div class="form-group"><label class="form-label">${esc(t('act.veld.status'))}</label>
        <select class="form-input" name="status">${STATUSSEN.map(s => optie(s, t('act.status.' + s), v.status === s)).join('')}</select></div>
    </div>
    ${veld('naam', t('act.veld.naam'), 'text', `placeholder="${esc(t('act.veld.naam.voorbeeld'))}"`)}
    <div class="form-row">${veld('locatie', t('act.veld.locatie'))}${veld('tarief_eur', t('act.veld.tarief'), 'number', 'min="0" step="0.01"')}</div>
    <div class="form-row">${veld('start_datum', t('act.veld.start'), 'date')}${veld('eind_datum', t('act.veld.eind'), 'date')}</div>
    <div class="form-row">${veld('inschrijven_tot', t('act.veld.inschrijventot'), 'date')}${veld('max_deelnemers', t('act.veld.max'), 'number', 'min="1" step="1"')}</div>
    <div class="section-title" style="margin-top:8px"><i class="ti ti-user-share"></i> ${esc(t('act.zelf'))}</div>
    <label style="display:flex;align-items:center;gap:8px;font-size:13px;margin-bottom:6px">
      <input type="checkbox" name="zelf_inschrijven" ${v.zelf_inschrijven ? 'checked' : ''}> ${esc(t('act.veld.zelf'))}</label>
    <label style="display:flex;align-items:center;gap:8px;font-size:13px;margin-bottom:6px">
      <input type="checkbox" name="akkoord_nodig" ${v.akkoord_nodig ? 'checked' : ''}> ${esc(t('act.veld.akkoord'))}</label>
    ${klein(t('act.zelf.uitleg'))}
    <div class="section-title" style="margin-top:8px"><i class="ti ti-filter"></i> ${esc(t('act.eisen'))}</div>
    ${klein(t('act.eisen.uitleg'))}
    <div class="form-row">${veld('min_leeftijd', t('act.veld.minleeftijd'), 'number', 'min="0" max="120"')}${veld('max_leeftijd', t('act.veld.maxleeftijd'), 'number', 'min="0" max="120"')}</div>
    ${bond() ? `<div class="form-row">
      <div class="form-group"><label class="form-label">${esc(t('act.veld.mingraad'))}</label>
        <select class="form-input" name="min_graad">${optie('', t('act.geen'), !v.min_graad)}${schaal.map(s => optie(s.code, s.naam, v.min_graad === s.code)).join('')}</select></div>
      <div class="form-group"><label class="form-label">${esc(t('act.veld.licentie'))}</label>
        <select class="form-input" name="licentie_soort_id">${optie('', t('act.geen'), !v.licentie_soort_id)}${soorten.map(s => optie(s.id, s.naam, v.licentie_soort_id === s.id)).join('')}</select></div>
    </div>
    <div class="form-group" data-alleen="cursus"><label class="form-label">${esc(t('act.veld.uitkomst'))}</label>
      <select class="form-input" name="uitkomst_licentie_id">${optie('', t('act.uitkomst.geen'), !v.uitkomst_licentie_id)}${soorten.map(s => optie(s.id, s.naam, v.uitkomst_licentie_id === s.id)).join('')}</select>
      ${klein(t('act.uitkomst.uitleg'))}</div>` : klein(t('act.eisen.geenbond'))}
    <div class="form-group"><label class="form-label">${esc(t('act.veld.omschrijving'))}</label>
      <textarea class="form-input" name="omschrijving" rows="2">${esc(v.omschrijving || '')}</textarea></div>`;
  const voet = `${!nieuw && heeft('activity.manage') && !(a.deelnemers > 0) ? knop('verwijder', 'trash', t('act.verwijderen'), 'btn-ghost', `data-id="${a.id}"`) : ''}
    <span style="margin-left:auto"></span>
    <button type="button" class="btn btn-default" data-act="sluit">${esc(t('alg.annuleren'))}</button>
    ${knop('opslaan', 'check', t('act.opslaan'), 'btn-primary', nieuw ? '' : `data-id="${a.id}"`)}`;
  toonModal(nieuw ? t('act.nieuw') : t('act.wijzigen'), inhoud, voet);
  toonVeldenVoorSoort();
}

function toonVeldenVoorSoort() {
  const m = document.getElementById('actModal');
  const soort = m?.querySelector('[name="kind"]')?.value;
  m?.querySelectorAll('[data-alleen]').forEach(el => { el.hidden = el.dataset.alleen !== soort; });
}

async function opslaan(id) {
  const el = document.getElementById('actModalInhoud');
  const getal = n => { const w = waarde(el, n); return w === '' ? null : Number(w); };
  const leeg = n => waarde(el, n) || null;
  const rij = {
    naam: waarde(el, 'naam').trim(),
    status: waarde(el, 'status'),
    locatie: leeg('locatie'),
    omschrijving: leeg('omschrijving'),
    start_datum: leeg('start_datum'),
    eind_datum: leeg('eind_datum'),
    inschrijven_tot: leeg('inschrijven_tot'),
    max_deelnemers: getal('max_deelnemers'),
    tarief_eur: getal('tarief_eur') ?? 0,
    min_leeftijd: getal('min_leeftijd'),
    max_leeftijd: getal('max_leeftijd'),
    zelf_inschrijven: !!el.querySelector('[name="zelf_inschrijven"]')?.checked,
    akkoord_nodig: !!el.querySelector('[name="akkoord_nodig"]')?.checked,
  };
  if (bond()) {
    rij.min_graad = leeg('min_graad');
    rij.licentie_soort_id = leeg('licentie_soort_id');
  }
  if (!rij.naam) { toast(t('act.fout.naam'), 'error'); return; }
  let res;
  if (id) {
    const oud = activiteiten.find(a => a.id === id) || {};
    if (bond()) rij.uitkomst_licentie_id = oud.kind === 'cursus' ? leeg('uitkomst_licentie_id') : null;
    res = await sb().from('activities').update(rij).eq('id', id).select('id').maybeSingle();
  } else {
    rij.kind = waarde(el, 'kind');
    rij.organisation_id = state.currentOrgId;
    if (bond()) rij.uitkomst_licentie_id = rij.kind === 'cursus' ? leeg('uitkomst_licentie_id') : null;
    res = await sb().from('activities').insert(rij).select('id').maybeSingle();
  }
  if (res.error) { fout(res.error); return; }
  sluitModal();
  toast(t('act.opgeslagen'), 'success');
  if (!id && res.data?.id) huidige = res.data.id;
  await render();
}

// ── Detail ─────────────────────────────────────────────────────────────────
async function renderDetail(el) {
  const [a, r] = await Promise.all([
    sb().from('activiteiten_overzicht').select('*').eq('id', huidige).maybeSingle(),
    sb().from('inschrijvingen_overzicht').select('*').eq('activity_id', huidige).order('naam'),
  ]);
  if (a.error || !a.data) { huidige = null; return renderLijst(el); }
  const act = a.data;
  const idx = activiteiten.findIndex(x => x.id === act.id);
  if (idx >= 0) activiteiten[idx] = act; else activiteiten.push(act);
  deelnemers = r.data || [];
  const actief = deelnemers.filter(d => d.status === 'ingeschreven');
  const aanvragen = deelnemers.filter(d => d.status === 'aangevraagd');
  const heeftUitslag = act.kind === 'examen' || act.kind === 'cursus';
  const beheer = heeft('activity.manage');

  const eisen = [
    act.min_leeftijd != null && act.max_leeftijd != null ? t('act.eis.leeftijd.tussen', { van: act.min_leeftijd, tot: act.max_leeftijd })
      : act.min_leeftijd != null ? t('act.eis.leeftijd.vanaf', { van: act.min_leeftijd })
      : act.max_leeftijd != null ? t('act.eis.leeftijd.tot', { tot: act.max_leeftijd }) : null,
    act.min_graad ? t('act.eis.graad', { graad: schaal.find(s => s.code === act.min_graad)?.naam || act.min_graad }) : null,
    act.licentie_soort_id ? t('act.eis.licentie', { licentie: soorten.find(s => s.id === act.licentie_soort_id)?.naam || '' }) : null,
    act.inschrijven_tot ? t('act.eis.tot', { datum: datum(act.inschrijven_tot) }) : null,
    act.zelf_inschrijven ? t(act.akkoord_nodig ? 'act.zelf.aan.akkoord' : 'act.zelf.aan') : null,
  ].filter(Boolean);

  const statusKnoppen = beheer ? [
    act.status === 'concept' || act.status === 'gesloten' ? knop('status', 'lock-open', t('act.naar.open'), 'btn-primary', 'data-status="open"') : '',
    act.status === 'open' ? knop('status', 'lock', t('act.naar.gesloten'), 'btn-default', 'data-status="gesloten"') : '',
    act.status === 'open' || act.status === 'gesloten' ? knop('status', 'flag-check', t('act.naar.afgerond'), 'btn-default', 'data-status="afgerond"') : '',
    act.status !== 'afgerond' && act.status !== 'geannuleerd' ? knop('status', 'ban', t('act.naar.geannuleerd'), 'btn-ghost', 'data-status="geannuleerd"') : '',
  ].join('') : '';

  el.innerHTML = `
    <div style="display:flex;align-items:center;gap:8px;margin-bottom:12px;flex-wrap:wrap">
      ${knop('terug', 'arrow-left', t('act.terug'), 'btn-ghost')}
    </div>
    <div class="settings-card">
      <div style="display:flex;align-items:flex-start;gap:12px;flex-wrap:wrap">
        <div style="flex:1;min-width:240px">
          <div style="font-size:18px;font-weight:600">${esc(act.naam)} ${statusBadge(act.status)}</div>
          <div style="font-size:13px;color:var(--text-2);margin-top:4px">${esc([t('act.soort.' + act.kind), periode(act), act.locatie].filter(Boolean).join(' · '))}</div>
          ${eisen.length ? `<div style="font-size:12px;color:var(--text-3);margin-top:6px">${esc(eisen.join(' · '))}</div>` : ''}
          ${act.omschrijving ? `<p style="font-size:13px;margin-top:8px">${esc(act.omschrijving)}</p>` : ''}
        </div>
        <div style="display:flex;gap:8px;flex-wrap:wrap">${beheer ? knop('wijzig', 'pencil', t('act.wijzigen')) : ''}${statusKnoppen}</div>
      </div>
      <div class="kpi-bar" style="margin:16px 0 0">
        <div class="kpi-card"><div class="kpi-label">${esc(t('act.kpi.deelnemers'))}</div><div class="kpi-value">${actief.length}${act.max_deelnemers ? ' / ' + act.max_deelnemers : ''}</div></div>
        <div class="kpi-card"><div class="kpi-label">${esc(t('act.kpi.tarief'))}</div><div class="kpi-value">${esc(geld(act.tarief_eur))}</div></div>
        <div class="kpi-card"><div class="kpi-label">${esc(t('act.kpi.ontvangen'))}</div><div class="kpi-value">${esc(geld(act.ontvangen_eur))}</div></div>
        <div class="kpi-card"><div class="kpi-label">${esc(t('act.kpi.openstaand'))}</div><div class="kpi-value">${esc(geld(act.openstaand_eur))}</div></div>
        ${heeftUitslag ? `<div class="kpi-card"><div class="kpi-label">${esc(t('act.kpi.uitslagen'))}</div><div class="kpi-value">${act.uitslagen ?? 0}</div></div>` : ''}
      </div>
    </div>
    <div style="display:flex;align-items:center;gap:8px;margin:16px 0 10px;flex-wrap:wrap">
      ${heeft('registration.manage') && act.status === 'open' ? knop('inschrijven', 'user-plus', t('act.inschrijven'), 'btn-primary') : ''}
      ${heeftUitslag && beheer && actief.length ? knop('uitslagen', 'list-check', t('act.uitslagen')) : ''}
      ${act.status !== 'open' && heeft('registration.manage') ? `<span style="font-size:12px;color:var(--text-3)">${esc(t('act.inschrijven.dicht'))}</span>` : ''}
    </div>
    ${aanvragen.length ? `<div class="banner banner-amber" style="margin-bottom:10px"><i class="ti ti-hourglass"></i>
      <span>${esc(t('act.aanvragen.banner', { aantal: aanvragen.length }))}</span></div>` : ''}
    <div id="actDeelnemers"></div>`;

  tabelTeken('deelnemers', 'actDeelnemers', [
    { veld: 'naam', label: t('act.kol.deelnemer'),
      cel: d => `<strong>${esc(d.naam)}</strong>${deelnemerBadges(d)}${d.geschorst ? ` <span class="badge badge-red">${esc(t('bond.geschorst'))}</span>` : ''}` },
    { veld: 'lidnummer', label: t('leden.kol.lidnummer'), mono: true },
    ...(bond() ? [{ veld: 'graad', label: t('bond.kol.graad') }] : []),
    { veld: 'bron', label: t('act.kol.bron'), tekst: d => t('act.bron.' + (d.bron || 'beheerder')) },
    { veld: 'payment_status', label: t('act.kol.betaling'), tekst: d => t('act.betaal.' + inschrijvingStatus(d)), cel: betaalBadge },
    { veld: 'balance_eur', label: t('act.kol.openstaand'), uitlijn: 'right', mono: true, filter: false, cel: d => esc(geld(d.balance_eur)) },
    ...(heeftUitslag ? [{ veld: 'uitslag', label: t('act.kol.uitslag'), tekst: uitslagTekst, cel: d => esc(uitslagTekst(d)) + uitslagBadge(d) }] : []),
  ], deelnemers, {
    zoekLabel: t('act.zoek.deelnemer'),
    leeg: t('act.deelnemers.leeg'),
    sorteerVeld: 'naam',
    rijKlik: d => openDeelnemer(d),
  });
}

async function zetStatus(status) {
  const act = activiteiten.find(a => a.id === huidige);
  const doe = async () => {
    const { error } = await sb().from('activities').update({ status }).eq('id', huidige);
    if (error) { fout(error); return; }
    toast(t('act.status.gewijzigd', { status: t('act.status.' + status) }), 'success');
    await render();
  };
  if (status === 'geannuleerd') showConfirm(t('act.naar.geannuleerd'), t('act.annuleren.bevestig', { naam: act?.naam || '' }), doe);
  else await doe();
}

// ── Inschrijven vanuit het ledenbestand ────────────────────────────────────
let kandidaten = [];
async function openInschrijven() {
  const { data, error } = await sb().from('persons').select('id, voornaam, tussenvoegsel, achternaam, lidnummer, geboortedatum')
    .eq('organisation_id', state.currentOrgId).is('anonymised_at', null).order('achternaam');
  if (error) { fout(error); return; }
  const al = new Set(deelnemers.filter(d => d.status === 'ingeschreven' || d.status === 'aangevraagd').map(d => d.person_id));
  kandidaten = (data || []).filter(p => !al.has(p.id)).map(p => ({
    ...p, naam: [p.voornaam, p.tussenvoegsel, p.achternaam].filter(Boolean).join(' '),
  }));
  const rijen = kandidaten.map(p => `<label class="eigendom-doc" data-kandidaat="${p.id}" data-tekst="${esc((p.naam + ' ' + (p.lidnummer || '')).toLowerCase())}">
      <input type="checkbox" data-persoon="${p.id}">
      <span>${esc(p.naam)} <span class="start-sub">${esc([p.lidnummer, datum(p.geboortedatum)].filter(Boolean).join(' · '))}</span></span></label>`).join('');
  toonModal(t('act.inschrijven'), `
    ${klein(t('act.inschrijven.uitleg'))}
    <div style="display:flex;gap:8px;align-items:center;margin:8px 0">
      <input class="form-input" data-zoek placeholder="${esc(t('act.zoek.lid'))}" style="flex:1">
      ${knop('allezichtbaar', 'checks', t('act.selecteer.zichtbaar'), 'btn-ghost')}
    </div>
    <div id="actKandidaten" style="max-height:50vh;overflow:auto">${rijen || klein(t('act.kandidaten.leeg'))}</div>
    <div id="actVerslag"></div>`,
    `<span style="margin-left:auto"></span>
     <button type="button" class="btn btn-default" data-act="sluit">${esc(t('alg.sluiten'))}</button>
     ${knop('inschrijvenbevestig', 'check', t('act.inschrijven.knop'), 'btn-primary')}`);
}

function filterKandidaten(zoek) {
  const naald = String(zoek || '').trim().toLowerCase();
  document.querySelectorAll('#actKandidaten [data-kandidaat]').forEach(el => {
    el.hidden = !!naald && !el.dataset.tekst.includes(naald);
  });
}

async function inschrijvenBevestig() {
  const ids = [...document.querySelectorAll('#actKandidaten [data-persoon]:checked')].map(c => c.dataset.persoon);
  if (!ids.length) { toast(t('act.inschrijven.niemand'), 'error'); return; }
  const { data, error } = await sb().rpc('inschrijven', { p_activiteit: huidige, p_personen: ids });
  if (error) { fout(error); return; }
  const gelukt = (data || []).filter(r => r.resultaat === 'ingeschreven');
  const mislukt = (data || []).filter(r => r.resultaat !== 'ingeschreven');
  const naam = id => kandidaten.find(p => p.id === id)?.naam || id;
  await render();
  if (!mislukt.length) {
    sluitModal();
    toast(t('act.inschrijven.gelukt', { aantal: gelukt.length }), 'success');
    return;
  }
  // Laat zien wie niet kon en waarom; de rest is ingeschreven.
  gelukt.forEach(r => document.querySelector(`#actKandidaten [data-kandidaat="${r.person_id}"]`)?.remove());
  document.getElementById('actVerslag').innerHTML = `
    <div class="banner banner-amber" style="margin-top:10px"><i class="ti ti-alert-triangle"></i>
      <span>${esc(t('act.inschrijven.deels', { gelukt: gelukt.length, mislukt: mislukt.length }))}</span></div>
    ${mislukt.map(r => `<div class="org-row" data-geweigerd="${r.person_id}"><div style="flex:1;font-size:13px">${esc(naam(r.person_id))}</div>
      <span style="font-size:12px;color:var(--text-2)">${esc(t('inschrijving.fout.' + r.resultaat))}</span></div>`).join('')}`;
}

// ── Deelnemer: betaling, afmelden, uitslag ─────────────────────────────────
function graadKeuze(naam, gekozen) {
  return `<select class="form-input" name="${naam}"><option value=""></option>${schaal
    .map(s => `<option value="${esc(s.code)}"${s.code === gekozen ? ' selected' : ''}>${esc(s.naam)}</option>`).join('')}</select>`;
}

function uitslagOpen(d) {
  if (d.status !== 'ingeschreven') return false;
  if (d.uitslag_status === 'voorgesteld') return false;
  return !(d.uitslag_status === 'vastgelegd' && d.uitslag === 'geslaagd');
}

function openDeelnemer(d) {
  const act = activiteiten.find(a => a.id === huidige) || {};
  const heeftUitslag = act.kind === 'examen' || act.kind === 'cursus';
  const fin = heeft('finance.write');
  const reden = naam => `<input class="form-input" name="${naam}" placeholder="${esc(t('bond.reden.voorbeeld'))}">`;
  const inhoud = `
    <div style="font-size:13px;color:var(--text-2);margin-bottom:10px">${esc([d.lidnummer, d.graad, t('act.bron.' + (d.bron || 'beheerder'))].filter(Boolean).join(' · '))}
      ${deelnemerBadges(d)}</div>
    ${d.status === 'aangevraagd' && heeft('registration.manage') ? `<div class="section-title"><i class="ti ti-hourglass"></i> ${esc(t('act.aanvraag'))}</div>
      ${klein(t('act.aanvraag.uitleg', { datum: datum(String(d.created_at || '').slice(0, 10)), door: d.created_by || '' }))}
      <div class="act-rij" style="margin-bottom:12px">
        <div class="form-group" style="flex:3"><input class="form-input" name="besluitreden" maxlength="500" placeholder="${esc(t('act.aanvraag.reden'))}"></div>
        ${knop('akkoord', 'check', t('act.aanvraag.akkoord'), 'btn-primary', `data-id="${d.id}"`)}
        ${knop('afwijzen', 'x', t('act.aanvraag.afwijzen'), 'btn-default', `data-id="${d.id}"`)}
      </div>` : ''}
    ${d.status === 'afgewezen' && d.besluit_reden ? klein(t('act.aanvraag.afgewezenom', { reden: d.besluit_reden })) : ''}

    <div class="section-title"><i class="ti ti-coin-euro"></i> ${esc(t('act.betaling'))}</div>
    <div class="act-bedragen">
      <div><span>${esc(t('act.kpi.tarief'))}</span><strong>${esc(geld(d.entry_fee_eur))}</strong></div>
      <div><span>${esc(t('act.korting'))}</span><strong>${esc(geld(d.discount_eur))}</strong></div>
      <div><span>${esc(t('act.betaald'))}</span><strong>${esc(geld(d.amount_paid_eur))}</strong></div>
      <div><span>${esc(t('act.kpi.openstaand'))}</span><strong>${esc(geld(d.balance_eur))}</strong></div>
    </div>
    <div>${betaalBadge(d)}${Number(d.manual_adjustment_eur) ? ` <span style="font-size:12px;color:var(--text-3)">${esc(t('act.correctie', { bedrag: geld(d.manual_adjustment_eur), reden: d.adjustment_reason || '' }))}</span>` : ''}</div>
    ${fin ? `<div class="act-rij" style="margin-top:10px">
      <div class="form-group"><label class="form-label">${esc(t('act.betaald'))}</label>
        <input class="form-input" type="number" min="0" step="0.01" name="betaald" value="${esc(String(d.amount_paid_eur ?? 0))}"></div>
      <div class="form-group"><label class="form-label">${esc(t('act.korting'))}</label>
        <input class="form-input" type="number" min="0" step="0.01" name="korting" value="${esc(String(d.discount_eur ?? 0))}"></div>
      ${knop('betaling', 'device-floppy', t('act.betaling.opslaan'), 'btn-default', `data-id="${d.id}"`)}
    </div>` : ''}
    ${heeft('finance.writeoff') && (Number(d.balance_eur) > 0 || Number(d.manual_adjustment_eur)) ? `<div class="act-rij" style="margin-top:8px">
      <div class="form-group" style="flex:3">${reden('afboekreden')}</div>
      ${Number(d.balance_eur) > 0 ? knop('afboeken', 'receipt-off', t('act.afboeken'), 'btn-default', `data-id="${d.id}"`) : ''}
      ${Number(d.manual_adjustment_eur) ? knop('terugdraaien', 'arrow-back-up', t('act.terugdraaien'), 'btn-ghost', `data-id="${d.id}"`) : ''}
    </div>` : ''}

    ${heeftUitslag ? `<div class="section-title" style="margin-top:14px"><i class="ti ti-certificate"></i> ${esc(t('act.kol.uitslag'))}</div>
      ${d.uitslag ? `<div style="font-size:13px">${esc(uitslagTekst(d))}${uitslagBadge(d)}${d.examinator ? ` <span style="font-size:12px;color:var(--text-3)">${esc(d.examinator)}</span>` : ''}</div>` : klein(t('act.uitslag.geen'))}
      ${d.uitslag_status === 'voorgesteld' ? klein(t('act.uitslag.wacht')) : ''}
      ${heeft('activity.manage') && uitslagOpen(d) ? `<div class="act-rij" style="margin-top:8px">
        <div class="form-group"><label class="form-label">${esc(t('act.kol.uitslag'))}</label>
          <select class="form-input" name="uitslag"><option value="geslaagd">${esc(t('act.uitslag.geslaagd'))}</option><option value="niet_geslaagd">${esc(t('act.uitslag.niet_geslaagd'))}</option></select></div>
        ${act.kind === 'examen' ? `<div class="form-group"><label class="form-label">${esc(t('act.nieuwegraad'))}</label>${graadKeuze('code', d.uitslag_code)}</div>` : ''}
        <div class="form-group"><label class="form-label">${esc(t('bond.examinator'))}</label><input class="form-input" name="examinator" value="${esc(d.examinator || '')}"></div>
        ${knop('uitslag', 'send', t('act.uitslag.vastleggen'), 'btn-primary', `data-id="${d.id}"`)}
      </div>
      ${klein(t(bond() ? 'act.uitslag.uitleg.bond' : 'act.uitslag.uitleg.club'))}` : ''}` : ''}`;
  const voet = `${heeft('registration.manage') && (d.status === 'ingeschreven' || d.status === 'aangevraagd') ? knop('afmelden', 'user-minus', t('act.afmelden'), 'btn-ghost', `data-id="${d.id}"`) : ''}
    <span style="margin-left:auto"></span>
    <button type="button" class="btn btn-default" data-act="sluit">${esc(t('alg.sluiten'))}</button>`;
  toonModal(d.naam, inhoud, voet);
}

async function betalingOpslaan(id) {
  const el = document.getElementById('actModalInhoud');
  const { error } = await sb().from('activity_registrations')
    .update({ amount_paid_eur: Number(waarde(el, 'betaald') || 0), discount_eur: Number(waarde(el, 'korting') || 0) }).eq('id', id);
  if (error) { fout(error); return; }
  toast(t('act.betaling.opgeslagen'), 'success');
  await naWijziging(id);
}

async function geld_dispatch(handeling, id) {
  const el = document.getElementById('actModalInhoud');
  const reden = waarde(el, 'afboekreden').trim();
  if (reden.length < 3) { toast(t('dispatch.fout.reden_ontbreekt'), 'error'); return; }
  const { data, error } = await sb().rpc('dispatch', { p_handeling: handeling, p_gegevens: { registration_id: id }, p_reden: reden });
  if (error) { fout(error); return; }
  toast(t(data?.status === 'wacht' ? 'vierogen.ingediend' : 'vierogen.uitgevoerd'), 'success');
  await vernieuwTeller();
  await naWijziging(id);
}

async function afmelden(id) {
  const d = deelnemers.find(x => x.id === id);
  showConfirm(t('act.afmelden'), t('act.afmelden.bevestig', { naam: d?.naam || '' }), async () => {
    const { error } = await sb().from('activity_registrations').update({ status: 'afgemeld' }).eq('id', id);
    if (error) { fout(error); return; }
    sluitModal();
    toast(t('act.afgemeld.gedaan'), 'success');
    await render();
  });
}

async function besluiten(id, akkoord) {
  const el = document.getElementById('actModalInhoud');
  const reden = waarde(el, 'besluitreden').trim() || null;
  const { error } = await sb().rpc('inschrijving_besluiten', { p_inschrijving: id, p_akkoord: akkoord, p_reden: reden });
  if (error) { fout(error); return; }
  toast(t(akkoord ? 'act.aanvraag.goedgekeurd' : 'act.aanvraag.afgewezen'), 'success');
  await naWijziging(id);
}

async function uitslagVastleggen(id, uitslag, code, examinator) {
  const { data, error } = await sb().rpc('uitslag_vastleggen', {
    p_inschrijving: id, p_uitslag: uitslag, p_code: code || null, p_examinator: examinator || null, p_reden: null,
  });
  if (error) return { fout: error };
  return { status: data?.status };
}

async function uitslagEen(id) {
  const el = document.getElementById('actModalInhoud');
  const uitslag = waarde(el, 'uitslag');
  const code = waarde(el, 'code');
  const act = activiteiten.find(a => a.id === huidige) || {};
  if (uitslag === 'geslaagd' && act.kind === 'examen' && !code) { toast(t('uitslag.fout.graad_ontbreekt'), 'error'); return; }
  const r = await uitslagVastleggen(id, uitslag, code, waarde(el, 'examinator'));
  if (r.fout) { fout(r.fout); return; }
  toast(t('act.uitslagstatus.gedaan.' + r.status), 'success');
  await vernieuwTeller();
  await naWijziging(id);
}

async function naWijziging(id) {
  await render();
  const d = deelnemers.find(x => x.id === id);
  if (d) openDeelnemer(d); else sluitModal();
}

// ── Uitslagen voor de hele lijst ───────────────────────────────────────────
function openUitslagen() {
  const act = activiteiten.find(a => a.id === huidige) || {};
  const open = deelnemers.filter(uitslagOpen);
  const rijen = open.map(d => `<div class="org-row" data-rij="${d.id}" style="gap:8px;flex-wrap:wrap">
      <div style="flex:1;min-width:160px;font-size:13px">${esc(d.naam)} <span class="start-sub">${esc(d.graad || '')}</span></div>
      <select class="form-input" name="u_${d.id}" style="width:auto"><option value=""></option>
        <option value="geslaagd">${esc(t('act.uitslag.geslaagd'))}</option><option value="niet_geslaagd">${esc(t('act.uitslag.niet_geslaagd'))}</option></select>
      ${act.kind === 'examen' ? `<span style="width:160px">${graadKeuze('g_' + d.id, d.uitslag_code)}</span>` : ''}
    </div>`).join('');
  toonModal(t('act.uitslagen'), `
    ${klein(t(bond() ? 'act.uitslag.uitleg.bond' : 'act.uitslag.uitleg.club'))}
    <div class="form-group"><label class="form-label">${esc(t('bond.examinator'))}</label><input class="form-input" name="examinator"></div>
    ${rijen || klein(t('act.uitslagen.leeg'))}
    <div id="actVerslag"></div>`,
    `<span style="margin-left:auto"></span>
     <button type="button" class="btn btn-default" data-act="sluit">${esc(t('alg.sluiten'))}</button>
     ${open.length ? knop('uitslagenbevestig', 'send', t('act.uitslag.vastleggen'), 'btn-primary') : ''}`);
}

async function uitslagenBevestig() {
  const el = document.getElementById('actModalInhoud');
  const act = activiteiten.find(a => a.id === huidige) || {};
  const examinator = waarde(el, 'examinator');
  const rijen = [...el.querySelectorAll('[data-rij]')].map(r => ({
    id: r.dataset.rij, uitslag: waarde(r, 'u_' + r.dataset.rij), code: act.kind === 'examen' ? waarde(r, 'g_' + r.dataset.rij) : null,
  })).filter(r => r.uitslag);
  if (!rijen.length) { toast(t('act.uitslagen.niets'), 'error'); return; }
  const mislukt = [];
  let gelukt = 0;
  // Na elkaar: elke uitslag is een eigen voorstel met een eigen controle.
  for (const r of rijen) {
    if (r.uitslag === 'geslaagd' && act.kind === 'examen' && !r.code) { mislukt.push({ ...r, melding: t('uitslag.fout.graad_ontbreekt') }); continue; }
    const uit = await uitslagVastleggen(r.id, r.uitslag, r.code, examinator);
    if (uit.fout) mislukt.push({ ...r, melding: activiteitFout(uit.fout) }); else gelukt++;
  }
  await vernieuwTeller();
  await render();
  if (!mislukt.length) { sluitModal(); toast(t('act.uitslagen.gelukt', { aantal: gelukt }), 'success'); return; }
  openUitslagen();
  const naam = id => deelnemers.find(d => d.id === id)?.naam || id;
  document.getElementById('actVerslag').innerHTML = `
    <div class="banner banner-amber" style="margin-top:10px"><i class="ti ti-alert-triangle"></i>
      <span>${esc(t('act.uitslagen.deels', { gelukt, mislukt: mislukt.length }))}</span></div>
    ${mislukt.map(r => `<div class="org-row"><div style="flex:1;font-size:13px">${esc(naam(r.id))}</div>
      <span style="font-size:12px;color:var(--text-2)">${esc(r.melding)}</span></div>`).join('')}`;
}

// ── Events ─────────────────────────────────────────────────────────────────
function klik(e) {
  const k = e.target.closest('[data-act]');
  if (!k) return;
  const id = k.dataset.id;
  switch (k.dataset.act) {
    case 'nieuw': openFormulier(); break;
    case 'wijzig': openFormulier(activiteiten.find(a => a.id === huidige)); break;
    case 'opslaan': opslaan(id); break;
    case 'verwijder': showConfirm(t('act.verwijderen'), t('act.verwijderen.bevestig'), async () => {
      const { error } = await sb().from('activities').delete().eq('id', id);
      if (error) { fout(error); return; }
      sluitModal(); huidige = null; toast(t('act.verwijderd'), 'success'); await render();
    }); break;
    case 'terug': huidige = null; render(); break;
    case 'status': zetStatus(k.dataset.status); break;
    case 'inschrijven': openInschrijven(); break;
    case 'allezichtbaar':
      document.querySelectorAll('#actKandidaten [data-kandidaat]:not([hidden]) [data-persoon]').forEach(c => { c.checked = true; });
      break;
    case 'inschrijvenbevestig': inschrijvenBevestig(); break;
    case 'betaling': betalingOpslaan(id); break;
    case 'afboeken': geld_dispatch('finance.writeoff', id); break;
    case 'terugdraaien': geld_dispatch('finance.reverse', id); break;
    case 'afmelden': afmelden(id); break;
    case 'akkoord': besluiten(id, true); break;
    case 'afwijzen': besluiten(id, false); break;
    case 'uitslag': uitslagEen(id); break;
    case 'uitslagen': openUitslagen(); break;
    case 'uitslagenbevestig': uitslagenBevestig(); break;
    default: break;
  }
}

function koppel(el) {
  if (el.dataset.gekoppeld) return;
  el.dataset.gekoppeld = '1';
  el.addEventListener('click', klik);
  document.addEventListener('taalgewijzigd', () => {
    if (document.getElementById('pageActiviteiten')?.classList.contains('active')) render();
  });
}

// Bij wisselen van organisatie: niet in het detail van een ander blijven.
export function wis() { huidige = null; activiteiten = []; deelnemers = []; }
